import { Component, ViewEncapsulation, OnInit, signal } from '@angular/core';
import { UserService } from '../../../../../shared/services/user.service';
import { TableModule } from 'primeng/table';
import { FloatLabelModule } from 'primeng/floatlabel';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HeadingComponent } from '../../../../../shared/components/heading/heading.component';
import { InputIconModule } from 'primeng/inputicon';
import { IconFieldModule } from 'primeng/iconfield';
import { InputTextModule } from 'primeng/inputtext';
import { CommonModule } from '@angular/common';
import { CheckboxModule } from 'primeng/checkbox';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
 DynamicDialogModule } from 'primeng/dynamicdialog';
import { ConfirmationDialogService } from '../../../../../shared/services/confirmation-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { CreateEditJobTypeModalComponent } from './modals/create/create-job-type.component';
import { ViewUsersModalComponent } from './modals/view-users/view-users-modal.component';
import { JobTypeService } from '../../../../../shared/services/job-type.service';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { SessionUser } from '../../../../../store/user/user.interface';
import { Store } from '@ngrx/store';
import { getSessionFirm } from '../../../../../store/user/user.selector';
import { HelperService } from '../../../../../shared/services/helper.service';
import { PipesModule } from '../../../../../shared/pipes/pipes.module';

@Component({
  selector: 'app-job-types',
  standalone: true,
  imports: [
    CheckboxModule,
    FloatLabelModule,
    DialogModule,
    ButtonModule,
    FormsModule,
    IconFieldModule,
    InputIconModule,
    HeadingComponent,
    InputTextModule,
    TableModule,
    CommonModule,
    ReactiveFormsModule,
    DynamicDialogModule,
    TooltipModule,
    TagModule,
    PipesModule
  ],
  templateUrl: './job-types.component.html',
  styleUrl: './job-types.component.scss',
  providers: [DialogService, DynamicDialogRef, DynamicDialogConfig],
})
export class JobTypesComponent implements OnInit {
  jobTypes: any;
  createForm!: FormGroup;
  firm: any;
  ref: DynamicDialogRef | undefined;
  showEditCreate: boolean = false;

  /**
   * MP-5638/MP-5641: may this user ADMINISTER taxonomy?
   *
   * Taxonomy administration is FM-only once a firm is part of a family. The
   * firm's own settings flag still decides whether the feature exists at all;
   * this is ANDed with it in the template so the create, edit, archive and
   * delete controls are hidden from a user the server would refuse, rather
   * than letting them submit into a 403.
   *
   * See `UserService.canManageTaxonomy()` for the rule and its one known gap.
   */
  readonly canManageTaxonomy = signal(false);

  user: any;
  row: any;
  constructor(
    private userService: UserService,
    public dialogService: DialogService,
    private confirmationDialogService: ConfirmationDialogService,
    private toastService: ToastService,
    private store: Store<SessionUser>,
    public config: DynamicDialogConfig,
    private jobTypeService: JobTypeService,
    public helperService: HelperService
  ) {}

  ngOnInit() {
    this.user = this.userService.getSessionUser();
    this.canManageTaxonomy.set(this.userService.canManageTaxonomy());
    this.getFirm();
    this.getFirmJobType();
  }

  getFirm(){
    this.store.select(getSessionFirm).subscribe((res) => {
      this.firm = res;
      this.showEditCreate = this.firm.isDynamicFirmSettingsEnabled || this.firm.isSingleJobTypeEnabled;
    });
  }

  getFirmJobType() {
    this.jobTypeService.getFirmJobType(this.user.firm._id, true).subscribe({
      next: (jobTypes) => {
        this.jobTypes = jobTypes;
      },
    });
  }
  getModalConfig(modalType: string, data?: any) {
    return {
      header: modalType,
      width: '30vw',
      contentStyle: { overflow: 'hidden' },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
      data: {
        ...data,
        buttonText: modalType,
      },
    };
  }

  onArchive($event: Event, jobTypes: any) {
    $event.stopPropagation();
    this.confirmationDialogService.confirm({
      target: $event.target as EventTarget,
      message:
        'You are about to ' + (!jobTypes.isArchived ? 'deactivate ' : 'activate ') + jobTypes.name + '.',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text',
      accept: async () => {
        if(!jobTypes.isArchived){
          const res = await this.jobTypeService.getJobTypeAssociations(jobTypes?._id);
          if (!Object.keys(res.associations).length) {
            this.jobTypeService.archiveJobType(jobTypes?._id).subscribe({
              next: (res) => {
                this.getFirmJobType();
              },
            });
            this.getFirmJobType();
          } else {
            this.showJobTypeModal(res.associations, $event);
          }
        } else {
          this.jobTypeService.unarchiveJobType(jobTypes._id).subscribe({
            next: (JobTypes) => {
              this.getFirmJobType();
            },
          });
        }
      },
    });
  }

  onDelete($event: Event, jobTypes: any) {
    $event.stopPropagation();
    this.confirmationDialogService.confirm({
      target: $event.target as EventTarget,
      message: 'Are you sure you want to delete ' + jobTypes?.name + '?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'p-button-text',
      accept: async () => {
        const res = await this.jobTypeService.getJobTypeAssociations(jobTypes?._id);
        // If there are associations, show the job type modal
        if (Object.keys(res.associations || res).length) {
          this.showJobTypeModal(res.associations || res, $event);
        }
        // If no associations but there are reviewers, show confirmation modal
        else if (res.reviewers.singleJobUsers.length > 0 || res.reviewers.allReviewersCount > 0) {
          this.showReviewersConfirmationModal(jobTypes, res.reviewers, $event);
        }
        // If no associations and no reviewers, delete directly
        else {
          this.deleteJobType(jobTypes._id);
        }
      },
    });
  }

  showCreateJobTypeModal($event: Event, jobType?: any) {
    $event.stopPropagation();
    this.ref = this.dialogService.open(
      CreateEditJobTypeModalComponent,
      this.getModalConfig('Create')
    );

    this.ref.onClose.subscribe((data) => {
      if (data) {
        this.jobTypeService
          .createJobType({
            ...data,
            isGlobal: false,
            firm: this.user.firm._id,
          })
          .subscribe({
            next: (res) => {
              this.getFirmJobType();
              this.toastService.showSuccess(' Created successfully.');
            },
          });
      }
    });
  }

  showEditJobTypeModal($event: Event, jobType?: any) {
    $event.stopPropagation();
    this.ref = this.dialogService.open(
      CreateEditJobTypeModalComponent,
      this.getModalConfig('Edit', jobType)
    );

    this.ref.onClose.subscribe((data) => {
      if (data) {
        this.jobTypeService
          .updateJobType(jobType._id, {
            ...data,
            isGlobal: false,
            firm: this.user.firm._id,
            id: jobType._id
          })
          .subscribe({
            next: (res) => {
              this.getFirmJobType();
              this.toastService.showSuccess(' Updated successfully.');
            },
          });
      }
    });
  }
  
  showJobTypeModal(res: any, $event: Event): void{
    const { roles = { count: 0, values: [] }, criterias = { count: 0, values: [] }, creds = { count: 0, values: [] } } = res;
  
    const rolesText = this.helperService.formatList("Project - Role", roles);
    const criteriasText = this.helperService.formatList("Criterias", criterias);
    const credsText = this.helperService.formatList("Credentials", creds);

    const message = [rolesText, criteriasText, credsText].filter(Boolean).join("<br>");

    this.confirmationDialogService.confirm({
      target: $event.target as EventTarget,
      message:`The job type is associated with the following:<br><br>${message}`,
      header: 'Cannot Delete/Deactivate Job Type',
      icon: 'none',
      acceptIcon: 'none',
      rejectVisible: false,
      acceptLabel: 'OK',
      accept: () => {}
    })
  }

  showReviewersConfirmationModal(jobType: any, reviewers: any, $event: Event): void {
    this.ref = this.dialogService.open(ViewUsersModalComponent, {
      header: 'Confirm Deletion',
      width: '50vw',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
      data: {
        users: reviewers.singleJobUsers,
        jobTypeName: jobType?.name,
        showDeleteOption: true,

        totalReviewersCountMessage: `There ${reviewers.allReviewersCount > 1 ? 'are currently ' + reviewers.allReviewersCount + ' candidates' : 'is currently 1 candidate'} with this job type in their profile. <strong>${jobType?.name}</strong> will be removed from their profile.`,

        deleteMessage: `There ${reviewers.singleJobUsers.length > 1 ? 'are ' + reviewers.singleJobUsers.length + ' candidates who have' : 'is 1 candidate who has'} <strong>${jobType?.name}</strong> as their only job type. Deleting <strong>${jobType?.name}</strong> will make these candidates ineligible for any offers going forward.`,

        onDelete: () => this.deleteJobType(jobType._id),
        actionLabel: 'Delete'

      },
    });
  }

  deleteJobType(jobTypeId: string): void {
    this.jobTypeService
      .deleteJobType(jobTypeId)
      .subscribe({
        next: (res) => {
          this.getFirmJobType();
          this.toastService.showSuccess('Job type deleted successfully.');
        },
        error: (error) => {
          this.toastService.showError('Failed to delete job type.');
        }
      });
  }

}
